import type { NavItem } from "@scott/ui-d2c/types";
import { fetchWithHeaders } from "@/utils/common.utils";
import { ANONYMOUS_USER_COMPONENTS_URL, BASE_API_URL } from "@/utils/constants";

type NavigationComponentsResponse = {
  components: string;
};

type Component = {
  uid?: string;
  name?: string;
};

export async function fetchNavigationComponents(siteUid: string, lang: string) {
  if (!BASE_API_URL) {
    throw new Error("BASE_API_URL is not set");
  }
  const url = `${BASE_API_URL}/${siteUid}/${ANONYMOUS_USER_COMPONENTS_URL}/NavBarComponent?lang=${lang}`;
  try {
    const response = await fetchWithHeaders(url);

    const data: NavigationComponentsResponse = await response.json();

    const componentsArray = data.components.split(",").map((item) => {
      const [uid, name] = item.trim().split(":");
      return { uid, name };
    });

    return parseNavigationItems(componentsArray, siteUid, lang);
  } catch (_error) {
    return null;
  }
}

async function parseNavigationItems(
  components: Component[],
  siteUid: string,
  lang: string
): Promise<NavItem[]> {
  const results = await Promise.allSettled(
    components.map(async (component) => {
      if (!(component.uid && component.name)) {
        return null;
      }
      const componentData = await fetchNavigationComponentData(
        component.uid,
        siteUid,
        lang
      );
      if (!componentData?.navigationNode) {
        return null;
      }
      const navNode = componentData.navigationNode;
      const navLink = componentData.link;
      const navItem: NavItem = {
        title: navNode.title || "",
        link: navNode.link || "",
        linkName: navNode.linkName || "",
        titleEN: navNode.titleEN || component.name || "",
        onSale: navLink.onSale,
        componentData,
      };
      return navItem;
    })
  );

  return results
    .filter(
      (result): result is PromiseFulfilledResult<NavItem | null> =>
        result.status === "fulfilled"
    )
    .map((result) => result.value)
    .filter((item): item is NavItem => item !== null);
}

const FETCH_TIMEOUT_MS = 10_000;

async function fetchNavigationComponentData(
  componentUid: string,
  siteUid: string,
  lang: string
) {
  if (!BASE_API_URL) {
    return null;
  }
  const url = `${BASE_API_URL}/${siteUid}/${ANONYMOUS_USER_COMPONENTS_URL}/${componentUid}?lang=${lang}`;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const componentData = await fetchWithHeaders(url, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const data = await componentData.json();
    return data;
  } catch (_error) {
    return null;
  }
}
